import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useHistory, useLocation } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { t } from "i18next";

// Components
import Card from "components/Card/Card";
import SearchEmployeeForm from "components/SearchEmployee/SearchEmployeeForm";
import SearchEmployeeResult from "components/SearchEmployee/SearchEmployeeResult";
import CardHeader from "components/Card/CardHeader";
import PrimaryButton from "components/Buttons/PrimaryButton";

// Hooks
import useSearchEmployee from "hooks/useSearchEmployee";
import { useMasterData } from "hooks/useMasterData";
import { getDetailCourse } from "hooks/useCourse";

// Constants and Utils
import {
  FIELD_SEARCH_EMPLOYEE_CODE,
  FIELD_SEARCH_EMPLOYEE_NAME,
  FIELD_SEARCH_EMPLOYEE_WORK_STATUS,
  FIELD_SEARCH_EMPLOYEE_TYPE,
  FIELD_SEARCH_EMPLOYEE_HIRING_DATE,
  FIELD_SEARCH_EMPLOYEE_ROLE_1,
  FIELD_SEARCH_EMPLOYEE_ROLE_2,
  FIELD_SEARCH_EMPLOYEE_JOB,
  FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_2,
  FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_3,
  FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_4,
  FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_5,
} from "constants";
import { customSearchEmployeeInput } from "utils";
import { useEmployeeStore } from "store/employeeStore";

const FORM_KEYS = [
  "name",
  "from",
  "to",
  "work_status",
  "role_1",
  "job_codes",
  "role_2",
  "employment_type_code",
  "code",
  "department_2_codes",
  "department_3_code",
  "department_4_code",
  "department_5_code",
  "employee_group",
];

const IndividualTrainingAssignEmployee = () => {
  // Use state instead redux store because of optimizing performance
  // Benchmark result:
  // - Redux store: around 70 ms
  // - State: around 0.001 to 0.005 ms
  const [employeeIdsCheckedState, setEmployeeIdsCheckedState] = useState([]);
  // Store initial selected employees to preserve them when saving
  const initialSelectedEmployeesRef = useRef([]);

  const location = useLocation();
  const history = useHistory();
  const courseId = location?.state?.courseId;

  const isFirst = useRef(true);

  const schema = yup.object().shape({
    from: yup.string().nullable(),
    to: yup
      .string()
      .nullable()
      .test(
        "error",
        t("messages.date_end"),
        (value, context) =>
          !(value && new Date(value) < new Date(context.parent.from))
      ),
  });

  const { data: dataCourse } = courseId
    ? getDetailCourse(courseId, true)
    : { data: null };
  const { data: masterData } = useMasterData();

  const { onSubmit, fetchAllDataEmployees } = useSearchEmployee();
  const { allEmployees } = useEmployeeStore((state) => state);
  const {
    setParamsListEmployee,
    resetAllEmployee,
    setSearchedEmployees,
    searchedEmployees,
  } = useEmployeeStore();

  const methods = useForm({
    defaultValues: FORM_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: null }),
      {}
    ),
    resolver: yupResolver(schema),
  });

  const submitAndClose = () => {
    // clear data selection search
    FORM_KEYS.map((formKey) => {
      methods.setValue(formKey, "");
    });

    onSubmit({});
    
    // Get all checked employees by merging:
    // 1. Employees from current search results that are checked
    // 2. Initial employees that are still checked
    // 3. Any other checked employees from allEmployees (fallback)
    const checkedIdsSet = new Set(employeeIdsCheckedState);
    
    // Get checked employees from current search results
    const checkedFromSearch = searchedEmployees
      .filter((employee) => checkedIdsSet.has(employee.id))
      .map((employee) => ({ id: employee.id, name: employee.name }));
    
    // Get checked employees from initial list that might not be in current search
    const checkedFromInitial = initialSelectedEmployeesRef.current
      .filter((employee) => checkedIdsSet.has(employee.id))
      .map((employee) => ({ id: employee.id, name: employee.name }));
    
    // Get any remaining checked employees from allEmployees (for newly checked employees)
    const checkedFromAll = allEmployees
      .filter((employee) => {
        const isChecked = checkedIdsSet.has(employee.id);
        const notInSearch = !searchedEmployees.some(e => e.id === employee.id);
        const notInInitial = !initialSelectedEmployeesRef.current.some(e => e.id === employee.id);
        return isChecked && (notInSearch || notInInitial);
      })
      .map((employee) => ({ id: employee.id, name: employee.name }));
    
    // Merge and remove duplicates
    const allCheckedMap = new Map();
    [...checkedFromInitial, ...checkedFromSearch, ...checkedFromAll].forEach(emp => {
      allCheckedMap.set(emp.id, emp);
    });
    
    const selectedEmployeeList = Array.from(allCheckedMap.values());

    if (courseId) {
      sessionStorage.setItem(
        "useSelectedList",
        JSON.stringify(selectedEmployeeList)
      );
      sessionStorage.setItem("courseId", JSON.stringify(courseId));
    } else {
      const data = {
        useSelectedList: selectedEmployeeList,
        ...location.state,
      };
      sessionStorage.setItem(
        "dataCreateCourseIndividual",
        JSON.stringify(data)
      );
    }

    history.goBack();
  };

  window.addEventListener("load", () => {
    sessionStorage.removeItem("useSelectedList");
    sessionStorage.removeItem("courseId");
    sessionStorage.removeItem("dataCreateCourseIndividual");
  });

  const initData = async () => {
    resetAllEmployee();
    const employees = await fetchAllDataEmployees();

    if (!location.state && !courseId) {
      setSearchedEmployees(employees);
    } else if (courseId) {
      // Load initial selected employees from sessionStorage if available
      const useSelectedListJson = sessionStorage.getItem("useSelectedList");
      if (useSelectedListJson) {
        try {
          const useSelectedList = JSON.parse(useSelectedListJson);
          if (useSelectedList && Array.isArray(useSelectedList) && useSelectedList.length > 0) {
            const employeeIdsSelected = useSelectedList.map((user) => user.id);
            const selectedUser = employees.filter((employee) =>
              employeeIdsSelected.includes(employee.id)
            );
            
            // Store initial selected employees for later merging
            initialSelectedEmployeesRef.current = selectedUser.map(user => ({
              id: user.id,
              name: user.name
            }));
            
            setEmployeeIdsCheckedState(employeeIdsSelected);
            setSearchedEmployees(selectedUser);
          }
        } catch (e) {
          console.error("Error parsing useSelectedList:", e);
        }
      }
    }
  };

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    const detailCourse = dataCourse?.result?.data;
    if (detailCourse) {
      const useSelectedListJson = sessionStorage.getItem("useSelectedList");
      const useSelectedList = useSelectedListJson ? JSON.parse(useSelectedListJson) : null;

      const selectedUser = useSelectedList
        ? allEmployees.filter((employee) =>
            useSelectedList.find((item) => item.id === employee.id)
          )
        : allEmployees.filter((employee) =>
            JSON.parse(detailCourse?.user_assigned_list || "[]").find(
              (item) => item.id === employee.id
            )
          );
      const employeeIdsSelected = selectedUser.map((user) => user.id);
      const selectedIds = JSON.stringify(employeeIdsSelected);

      // Store initial selected employees for later merging
      initialSelectedEmployeesRef.current = selectedUser.map(user => ({
        id: user.id,
        name: user.name
      }));

      setEmployeeIdsCheckedState(employeeIdsSelected);
      setSearchedEmployees(selectedUser);
      setParamsListEmployee((prev) => ({
        ...prev,
        id_selected_list: detailCourse?.is_required_all ? null : selectedIds,
        id_exception_list: detailCourse?.is_required_all ? selectedIds : null,
        no_data: 0,
        isPaging: detailCourse?.is_required_all ? 1 : 0,
      }));
    }
  }, [dataCourse, allEmployees]);

  useEffect(() => {
    if (!courseId && location.state) {
      const dataCourseStore = location.state;
      const useSelectedListJson = location.state.user_selected_list;

      if (!useSelectedListJson) {
        return;
      }

      const useSelectedList = JSON.parse(useSelectedListJson);
      const employeeIdsSelected = useSelectedList.map((user) => user.id);
      const selectedUser = employeeIdsSelected?.length
        ? allEmployees.filter((employee) =>
            employeeIdsSelected.includes(employee.id)
          )
        : allEmployees;
      const selectedIds = JSON.stringify(employeeIdsSelected);
      
      // Store initial selected employees for later merging
      initialSelectedEmployeesRef.current = selectedUser.map(user => ({
        id: user.id,
        name: user.name
      }));
      
      setSearchedEmployees(selectedUser);
      setEmployeeIdsCheckedState(employeeIdsSelected);
      setParamsListEmployee((prev) => ({
        ...prev,
        id_selected_list: dataCourseStore?.is_required_all ? null : selectedIds,
        id_exception_list: dataCourseStore?.is_required_all
          ? selectedIds
          : null,
        no_data: 0,
        isPaging: dataCourseStore?.is_required_all ? 1 : 0,
      }));
    }
  }, [allEmployees, courseId, location.state]);

  useEffect(() => {
    // delay set isFirst when loading create. is create dataEmployees = blank
    setTimeout(() => {
      isFirst.current = false;
    }, 1000);

    if (!location?.state?.user_selected_list && isFirst.current) return;
  }, [searchedEmployees]);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "40px" }}>
      <Card>
        <CardHeader p="6px 0px 20px 0px">
          <Text fontSize="xl" fontWeight="bold">
            {t("pages.individual.individual_assign_employee")}
          </Text>
        </CardHeader>
        <SearchEmployeeForm
          methods={methods}
          inputList={[
            FIELD_SEARCH_EMPLOYEE_CODE,
            FIELD_SEARCH_EMPLOYEE_NAME,
            FIELD_SEARCH_EMPLOYEE_WORK_STATUS,
            customSearchEmployeeInput(FIELD_SEARCH_EMPLOYEE_TYPE, {
              data: [{ id: "", name: "" }].concat(
                masterData?.employment_types ?? []
              ),
            }),
            FIELD_SEARCH_EMPLOYEE_HIRING_DATE,
            FIELD_SEARCH_EMPLOYEE_ROLE_1,
            FIELD_SEARCH_EMPLOYEE_ROLE_2,
            customSearchEmployeeInput(FIELD_SEARCH_EMPLOYEE_JOB, {
              label: "pages.course_form.job_type",
              data: [{ id: "", name: "" }].concat(masterData?.jobs ?? []),
            }),
            customSearchEmployeeInput(FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_2, {
              data: [{ id: "", name: "" }].concat(
                masterData?.deparment_layer_2 ?? []
              ),
            }),
            customSearchEmployeeInput(FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_3, {
              data: [{ id: "", name: "" }].concat(
                masterData?.deparment_layer_3 ?? []
              ),
            }),
            customSearchEmployeeInput(FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_4, {
              data: [{ id: "", name: "" }].concat(
                masterData?.deparment_layer_4 ?? []
              ),
            }),
            customSearchEmployeeInput(FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_5, {
              data: [{ id: "", name: "" }].concat(
                masterData?.deparment_layer_5 ?? []
              ),
            }),
          ]}
          col={3}
          submitAndClose={submitAndClose}
          handleSubmit={methods.handleSubmit(onSubmit)}
          isModal
          isHideSubmitAndClose={true}
        />
        <SearchEmployeeResult
          maxHeigh="full"
          employeeIdsCheckedState={employeeIdsCheckedState}
          setEmployeeIdsCheckedState={setEmployeeIdsCheckedState}
        />
        <Flex justifyContent="center" pb="20px">
          <Box mr={"10px"}>
            <PrimaryButton
              text={t("pages.course_form.add_member")}
              onClick={submitAndClose}
            />
          </Box>
        </Flex>
      </Card>
    </Flex>
  );
};

export default IndividualTrainingAssignEmployee;
